import { type SiteMaterialDetail, useApiTradeV1TradeSitesRetrieve } from 'api';
import {
  AllowedMaterialSpecAutocomplete,
  Form,
  MaterialProductAutocomplete,
  TextField,
  TradeCompanySiteSelector,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Button } from 'ui';

import { useUpdateSiteMaterial } from '../../hooks/useUpdateSiteMaterial/useUpdateSiteMaterial';

type Props = {
  siteMaterialId: number;
  siteMaterialDetails: SiteMaterialDetail;
};

const schema = yup.object().shape({
  material_grade_id: yup.number().optional(),
  material_product_id: yup.number().required(),
  material_spec_id: yup.number().optional().nullable(),
  seller_label: yup.string().optional(),
  site_id: yup.number().required(),
});

type FormValues = yup.InferType<typeof schema>;

export const SiteMaterialUpdateForm = ({ siteMaterialDetails, siteMaterialId }: Props) => {
  const { t } = useTranslation('orders');
  const form = useForm({
    defaultValues: {
      material_product_id: siteMaterialDetails.material_product?.id,
      material_spec_id: siteMaterialDetails.material_spec.id,
      seller_label: siteMaterialDetails.seller_label || undefined,
      site_id: siteMaterialDetails.site.id,
    },
    resolver: yupResolver(schema),
  });
  const [materialSpecId, setMaterialSpecId] = useState(siteMaterialDetails.material_spec.id);
  // We shouldn't do a second request here, we should use the data from the trade site autocomplete instead.
  // Unfortunately, the autocomplete field doesn't return the BE value received from the initialValue
  // and can't be accessed like the one when the field is updated( onSelectOption).
  const { data: tradeSiteDetails } = useApiTradeV1TradeSitesRetrieve(siteMaterialDetails.site.id);
  const { updateSiteMaterial, isPending } = useUpdateSiteMaterial();
  const onSubmit = async (values: FormValues) => {
    await updateSiteMaterial(siteMaterialId, values.material_product_id, values.seller_label);
  };

  return (
    <Form {...form} onSubmit={onSubmit}>
      <TradeCompanySiteSelector
        name="site_id"
        label={t('siteMaterials.form.labels.site')}
        tradeCompanyId={siteMaterialDetails.site.trade_company.id}
        disabled
      />
      <AllowedMaterialSpecAutocomplete
        name="material_spec_id"
        label={t('siteMaterials.form.labels.materialSpec')}
        onSelectOption={(option) => setMaterialSpecId(option.id)}
      />
      <MaterialProductAutocomplete
        name="material_product_id"
        label={t('siteMaterials.form.labels.materialProduct')}
        country={tradeSiteDetails?.country}
        material_spec_id={materialSpecId}
        required
      />
      <TextField
        label={t('siteMaterials.form.labels.sellerLabel')}
        name="seller_label"
        infoTooltip={t('siteMaterials.form.tooltips.sellerLabel')}
      />
      <Button loading={isPending} type="submit">
        {t('siteMaterials.form.actions.update')}
      </Button>
    </Form>
  );
};
